/**
 * InlineProfileEditor: embedded editor used inside the Profiles list row
 */

import React, { useEffect, useRef, useState } from 'react';
import { Save, Zap, Settings, Palette, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useProfile } from '@/hooks/useProfiles';
import { useGroupedTemplates } from '@/hooks/useTemplates';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import AllowedStylesPanel from '@/components/profiles/AllowedStylesPanel';
import StageSettingsGrid from '@/components/profiles/StageSettingsGrid';
import PresetLoaderModal from '@/components/profiles/PresetLoaderModal';
import { cn } from '@/utils/cn';
import type { StageSettingsInput, ValidationError } from '@/hooks/useProfiles';
import type { TemplateReference } from '@/hooks/useTemplates';
import { useDashboardServerStore } from '@/stores/dashboard-server';

const ExpandableStyleDNA: React.FC<{
  stylesCatalog: TemplateReference[];
  selected: string[];
  onChange: (selectedSlugs: string[]) => void;
}> = ({ stylesCatalog, selected, onChange }) => {
  const [expanded, setExpanded] = useState(true);

  

  return (
    <>
      <CardHeader 
        title="STYLE DNA" 
        subtitle="Visual styles to customize outputs and create consistent mood" 
        subtitleClassName="text-white/50"
        icon={<Palette className="w-5 h-5" />} 
        className="cursor-pointer"
        onClick={() => setExpanded(prev => !prev)}
        actions={
          <div className="text-xs font-mono text-white/60 px-2 py-1 bg-white/10 rounded border border-white/20">
            {selected.length} of 3
          </div>
        }
      />
      <CardContent>
        <motion.div layout transition={{ duration: 0.6, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
          <AllowedStylesPanel
            availableStyles={stylesCatalog}
            selectedStyles={selected}
            onChange={onChange}
            collapsed={!expanded}
          />
        </motion.div>
      </CardContent>
    </>
  );
};

interface InlineProfileEditorProps {
  profileId: string;
  onSaved?: () => void;
  onCancel?: () => void;
}

const InlineProfileEditor: React.FC<InlineProfileEditorProps> = ({ profileId, onSaved, onCancel }) => {
  const [showPreset, setShowPreset] = useState(false);
  const [hasDirty, setHasDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const loadPresetBtnRef = useRef<HTMLButtonElement>(null);
  const { addNotification } = useDashboardServerStore();

  // Data hooks
  const { profile, stageSettings, allowedStyles, loading, error, refetch, updateProfile, replaceStageSettings, replaceAllowedStyles, loadPreset } = useProfile(profileId);
  const { templates } = useGroupedTemplates();

  // Local form state
  const [form, setForm] = useState({ name: '', description: '' });
  const [stageForm, setStageForm] = useState<Record<string, StageSettingsInput>>({});
  const [styles, setStyles] = useState<string[]>([]);

  // Initialize
  useEffect(() => {
    if (profile) setForm({ name: profile.name, description: profile.description || '' });
  }, [profile]);

  useEffect(() => {
    if (stageSettings.length > 0) {
      const map: Record<string, StageSettingsInput> = {};
      stageSettings.forEach(s => {
        map[s.stage] = {
          stage: s.stage,
          writer_persona_id: s.writer_persona_id,
          evaluator_persona_id: s.evaluator_persona_id,
          rubric_id: s.rubric_id,
          provider: s.provider,
          model: s.model,
          default_style_id: s.default_style_id
        } as any;
      });
      setStageForm(map);
    }
  }, [stageSettings]);

  useEffect(() => {
    if (allowedStyles.length > 0) setStyles(allowedStyles.map(s => s.style_id));
  }, [allowedStyles]);

  const onChangeForm = (field: 'name' | 'description', value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasDirty(true);
  };

  const onChangeStage = (stage: string, field: string, value: string) => {
    setStageForm(prev => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        stage: stage as any,
        [field]: value
      }
    }));
    setHasDirty(true);
  };

  const onChangeStyles = (vals: string[]) => { setStyles(vals); setHasDirty(true); };

  const onSave = async () => {
    try {
      setSaving(true);
      setValidationErrors([]);
      await updateProfile({ name: form.name, description: form.description || null } as any);
      const stages = Object.values(stageForm).filter(s => s.stage);
      const resultStages = await replaceStageSettings(stages);
      if (!resultStages.success && resultStages.errors) {
        setValidationErrors(resultStages.errors);
        addNotification({ type: "error", title: 'Fix validation errors', duration: 4000 });
        return;
      }
      const resultStyles = await replaceAllowedStyles(styles);
      if (!resultStyles.success && resultStyles.errors) {
        setValidationErrors(resultStyles.errors);
        addNotification({ type: "error", title: 'Fix validation errors', duration: 4000 });
        return;
      }
      setHasDirty(false);
      addNotification({ type: "success", title: 'Profile saved', duration: 3000 });
      refetch();
      onSaved?.();
    } catch (e) {
      addNotification({ type: "error", title: 'Save failed', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const onLoadPreset = async (presetKey: string) => {
    const res = await loadPreset(presetKey as any);
    if (res.success) { setShowPreset(false); setHasDirty(false); await refetch(); }
  };

  const resetLocal = () => {
    if (profile) setForm({ name: profile.name, description: profile.description || '' });
    if (stageSettings.length > 0) {
      const map: Record<string, StageSettingsInput> = {};
      stageSettings.forEach(s => {
        map[s.stage] = {
          stage: s.stage,
          writer_persona_id: s.writer_persona_id,
          evaluator_persona_id: s.evaluator_persona_id,
          rubric_id: s.rubric_id,
          provider: s.provider,
          model: s.model,
          default_style_id: s.default_style_id
        } as any;
      });
      setStageForm(map);
    }
    if (allowedStyles.length > 0) setStyles(allowedStyles.map(s => s.style_id));
    setHasDirty(false);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="loading-spinner w-5 h-5 border-cyan-400"></div>
          <span className="text-cyan-400 font-mono text-sm">LOADING PROFILE...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-red-400 font-mono">ERROR: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {validationErrors.length > 0 && (
        <HolographicCard glow="error" intensity="subtle">
          <CardContent>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-red-400 font-mono text-sm mb-2">VALIDATION ERRORS</h3>
                <ul className="space-y-1">
                  {validationErrors.map((err, idx) => (
                    <li key={idx} className="text-white/70 text-sm">
                      <span className="text-red-400">{err.field}:</span> {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </HolographicCard>
      )}

      {/* Basics */}
      <HolographicCard glow="blue" intensity="subtle">
        <CardHeader 
          title="PROFILE SEED" 
          subtitle="Basic profile details to keep personas on brand" 
          subtitleClassName="text-white/50"
          icon={<Settings className="w-5 h-5" />} 
          actions={(
            <button ref={loadPresetBtnRef} type="button" onClick={() => setShowPreset(true)} className="btn-future text-purple-400 border-purple-400/50 hover:bg-purple-400/10 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">LOAD PRESET</span>
            </button>
          )}
        />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">Profile Name *</label>
              <input
                type="text"
                value={form.name || profile?.name || ''}
                onChange={(e) => onChangeForm('name', e.target.value)}
                className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3 text-white/90 font-mono text-sm focus:outline-none focus:border-cyan-500/50 focus:bg-black/70 transition-all"
                maxLength={100}
                required
                placeholder="Brand or descriptive name..."
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">Description</label>
              <textarea
                value={form.description || profile?.description || ''}
                onChange={(e) => onChangeForm('description', e.target.value)}
                className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3 text-white/90 font-mono text-sm focus:outline-none focus:border-cyan-500/50 focus:bg-black/70 transition-all resize-none"
                rows={3}
                maxLength={500}
                placeholder="Injected into persona prompts to help customize outputs..."
              />
            </div>
          </div>
        </CardContent>
      </HolographicCard>

      {/* Allowed Styles - Expandable */}
      <HolographicCard glow="purple" intensity="subtle">
        <ExpandableStyleDNA 
          stylesCatalog={templates.styles} 
          selected={styles} 
          onChange={onChangeStyles} 
        />
      </HolographicCard>

      {/* Stage Grid */}
      <HolographicCard glow="cyan" intensity="subtle">
        <CardHeader title="PERSONAS" subtitle="Configure personas, rubrics, and providers for each pipeline stage" subtitleClassName="text-white/50" icon={<Settings className="w-5 h-5" />} />
        <CardContent>
          <StageSettingsGrid stageSettings={stageForm} templates={templates} onChange={onChangeStage} />
        </CardContent>
      </HolographicCard>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => { resetLocal(); onCancel?.(); }} className="btn-future text-white/70 border-white/20 hover:bg-white/10">
          CANCEL
        </button>
        <button type="button" onClick={onSave} disabled={!hasDirty || saving} className={cn('btn-future flex items-center gap-2', hasDirty ? 'text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10' : 'text-white/30 border-white/10 cursor-not-allowed')}>
          {saving ? (
            <>
              <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin" />
              <span className="hidden sm:inline">SAVING...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">SAVE CHANGES</span>
            </>
          )}
        </button>
      </div>

      {showPreset && (
        <PresetLoaderModal anchorRef={loadPresetBtnRef} onLoadPreset={onLoadPreset} onClose={() => setShowPreset(false)} />
      )}
    </div>
  );
};

export default InlineProfileEditor;


